/**
 * 画布图片大图预览灯箱：portal 到 body 的全屏遮罩。
 *
 * - 左右箭头 / 键盘 ← → 翻页，Esc 或点遮罩关闭
 * - 底部序号指示（n / N），可选每张标签
 * - 右上角关闭，右上工具条提供下载
 * 供图片组节点等需要"点开放大看图"的场景复用。
 */
import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';

export interface LightboxImage {
  url: string;
  label?: string;
}

interface ImageLightboxProps {
  images: LightboxImage[];
  /** 当前显示的图片下标（在 images 数组内） */
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** 可选：下载当前图（不传则不显示下载按钮） */
  onDownload?: (image: LightboxImage, index: number) => void;
}

export function ImageLightbox({ images, index, onIndexChange, onClose, onDownload }: ImageLightboxProps) {
  const total = images.length;
  const go = useCallback(
    (delta: number) => {
      if (total === 0) return;
      onIndexChange((index + delta + total) % total);
    },
    [index, total, onIndexChange]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  if (total === 0) return null;
  const current = images[Math.min(Math.max(index, 0), total - 1)];

  return createPortal(
    <div
      className="fixed inset-0 z-[10002] flex select-none flex-col bg-black/85 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* 顶部工具条：序号 + 标签 + 下载 + 关闭 */}
      <div
        className="flex items-center justify-between px-4 py-3 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-sm text-white/80">
          {index + 1} / {total}
          {current.label ? ` · ${current.label}` : ''}
        </span>
        <div className="flex items-center gap-2">
          {onDownload && (
            <button
              type="button"
              onClick={() => onDownload(current, index)}
              className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
              title="下载当前图片"
            >
              <Download className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            title="关闭 (Esc)"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* 主图区：左右翻页 + 大图 */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-12 pb-10">
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                go(-1);
              }}
              className="absolute left-2 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
              title="上一张 (←)"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                go(1);
              }}
              className="absolute right-2 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
              title="下一张 (→)"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}
        <img
          src={current.url}
          alt={current.label || `图片 ${index + 1}`}
          className="max-h-full max-w-full rounded-md object-contain shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          draggable={false}
        />
      </div>

      {/* 多图时的底部缩略指示条 */}
      {total > 1 && (
        <div
          className="flex items-center justify-center gap-2 px-4 pb-4"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onIndexChange(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-6 bg-white' : 'w-1.5 bg-white/30 hover:bg-white/60'
              }`}
              title={img.label || `第 ${i + 1} 张`}
            />
          ))}
        </div>
      )}
    </div>,
    document.body
  );
}
